import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useSearchParams } from 'react-router-dom';
import type {
  ExtractedData,
  Invoice,
  ReferenceData,
  Supplier,
  ValidationFormState,
  ZohoBillTemplate,
} from '../../types';
import { invoicesApi, expertsApi, vendorAliasesApi } from '../../services/api';
import { buildInitialForm, fieldConfidence } from '../../lib/initial-form';
import { matchZohoVendor } from '../../lib/match-zoho-vendor';
import { matchTemplateLines } from '../../lib/match-template-lines';
import { consolidateLines } from '../../lib/consolidate-lines';
import { formatApiError } from '../../lib/api-error';
import { useZohoVendors } from '../../hooks/useZohoVendors';
import { useZohoItems } from '../../hooks/useZohoItems';
import { useZohoAccounts } from '../../hooks/useZohoAccounts';
import { useZohoLastBill } from '../../hooks/useZohoLastBill';
import { useCurrencyRate } from '../../hooks/useCurrencyRate';
import { Field, Select, TextInput } from '../shared/Field';
import { Spinner } from '../shared/Spinner';
import { VendorSelect } from '../shared/VendorSelect';
import { ItemSelect } from '../shared/ItemSelect';
import { LineItemsTable } from './LineItemsTable';
import { BillableSessionsPanel } from './BillableSessionsPanel';
import { RejectModal } from './RejectModal';
import { DeleteModal } from './DeleteModal';
import { useToast } from '../../context/ToastContext';
import { tauxEtranger, tvaNonDeductible } from '../../../server/lib/zoho-france';
import {
  ecartAvecTtcLu,
  lignesEnTtc,
  motifLignesSansTaux,
  peutDeclarerTvaNonRecuperable,
  tvaPresenteEffective,
} from '../../../server/lib/regime-tva';

/**
 * Sources possibles pour les lignes affichées : extraction Claude (cas
 * général, NDF, nouveau fournisseur, ou simplement vendor sans historique)
 * vs. duplication d'une bill Zoho précédente.
 */
type LinesSource =
  | { kind: 'claude' }
  | { kind: 'duplicated'; billNumber: string; billDate: string | null };

interface Props {
  invoice: Invoice;
  /** Suppliers locaux : conservés pour l'apprentissage des règles (auto). */
  suppliers: Supplier[];
  referenceData: ReferenceData;
}

export function ValidationForm({ invoice, suppliers, referenceData }: Props) {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const toast = useToast();
  const [searchParams] = useSearchParams();
  const returnQs = searchParams.get('return');
  const fromFilter = searchParams.get('from');
  const backToList = returnQs
    ? `/factures/liste?${decodeURIComponent(returnQs)}`
    : fromFilter
      ? `/factures/liste?status=${fromFilter}`
      : '/factures/liste';

  // ─── Parse les JSON stockés en base ───
  const extracted = useMemo<ExtractedData | null>(() => {
    if (!invoice.extracted_data) return null;
    try {
      return JSON.parse(invoice.extracted_data) as ExtractedData;
    } catch {
      return null;
    }
  }, [invoice.extracted_data]);

  const validated = useMemo<ValidationFormState | null>(() => {
    if (!invoice.validated_data) return null;
    try {
      return JSON.parse(invoice.validated_data) as ValidationFormState;
    } catch {
      return null;
    }
  }, [invoice.validated_data]);

  // ─── Source du flux : pour les NDF (Dominique, Fabien…), on ne duplique PAS
  //     la last-bill ───
  const isNdf = invoice.source?.startsWith('dropbox_ndf_') ?? false;

  // ─── Devise détectée par Claude ───────────────────────────────────────────
  const extractedCurrency = (extracted?.invoice.currency ?? 'EUR').toUpperCase();
  const isForeignCurrency = extractedCurrency !== 'EUR';
  const currencyRateQ = useCurrencyRate(isForeignCurrency ? extractedCurrency : null);

  // ─── Chargement Zoho ───
  const vendorsQ = useZohoVendors();
  const itemsQ = useZohoItems();
  const accountsQ = useZohoAccounts();
  const vendors = vendorsQ.data?.vendors ?? [];
  const items = itemsQ.data?.items ?? [];
  const accounts = accountsQ.data?.accounts ?? [];

  // ─── État du formulaire ───
  const [form, setForm] = useState<ValidationFormState>(() => {
    const initial = buildInitialForm({ validated, extracted, supplier: null, isNdf });
    // Upload manuel avec hint vendor : on pré-remplit zoho_vendor_id depuis
    // source_reference ("zoho_vendor:<id>"). Le matcher automatique skip
    // alors le matching depuis le nom Claude.
    if (
      !validated &&
      invoice.source === 'manual_upload' &&
      typeof invoice.source_reference === 'string' &&
      invoice.source_reference.startsWith('zoho_vendor:')
    ) {
      initial.zoho_vendor_id = invoice.source_reference.slice('zoho_vendor:'.length);
    }
    return initial;
  });
  const [rejectOpen, setRejectOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [autoMatchTried, setAutoMatchTried] = useState(false);
  const [ndfPrefillDone, setNdfPrefillDone] = useState(false);
  // Source des lignes affichée à l'utilisateur (bandeau). Par défaut Claude.
  const [linesSource, setLinesSource] = useState<LinesSource>({ kind: 'claude' });
  // Lignes dont la structure (produit/compte) n'a PAS pu être reprise de la
  // last-bill sans ambiguïté → laissées vides + avertissement à l'écran.
  const [ambiguousLines, setAmbiguousLines] = useState(0);

  // ─── Match automatique extraction → vendor Zoho (une seule fois quand
  //     la liste de vendors arrive) ───
  useEffect(() => {
    if (autoMatchTried) return;
    if (vendors.length === 0) return;
    setAutoMatchTried(true);

    // Si l'utilisateur a déjà sélectionné, ne touche pas.
    if (form.zoho_vendor_id) return;

    const m = matchZohoVendor(extracted?.supplier, vendors);
    if (m && m.confidence === 'high') {
      setForm((f) => ({ ...f, zoho_vendor_id: m.vendor.id, zoho_vendor_name: m.vendor.name }));
    }
    // confidence === 'medium' → on laisse vide et l'utilisateur choisit
  }, [vendors, extracted, autoMatchTried, form.zoho_vendor_id]);

  // ─── Last-bill du vendor sélectionné (template de duplication) ───
  // On désactive la duplication uniquement pour les NDF Dominique (chaque
  // justificatif est unique). Pour les factures en DEVISE ÉTRANGÈRE on
  // duplique quand même : on ne reprend que la STRUCTURE (produit, compte,
  // custom_fields) ; les montants viennent de Claude et sont convertis en
  // EUR par l'effet de conversion. Les deux effets coexistent sans conflit
  // (chacun idempotent via son flag).
  const lastBillQ = useZohoLastBill(
    isNdf || form.is_new_vendor ? null : form.zoho_vendor_id,
  );

  // Quand last-bill arrive et qu'on n'a pas encore appliqué de template à
  // ce vendor, on importe les lignes en gardant les montants extraits.
  const [lastBillAppliedFor, setLastBillAppliedFor] = useState<string | null>(null);
  useEffect(() => {
    if (isNdf) return;
    if (validated) return;
    if (!form.zoho_vendor_id) {
      console.debug('[lastbill] pas de vendor sélectionné → pas de duplication');
      return;
    }
    if (lastBillQ.isFetching) return;
    if (!lastBillQ.data) return;
    if (!lastBillQ.data.bill) {
      console.debug(
        `[lastbill] vendor ${form.zoho_vendor_id} sans historique (has_history=${lastBillQ.data.has_history})`,
      );
      return;
    }
    if (lastBillAppliedFor === form.zoho_vendor_id) return;
    console.debug(
      `[lastbill] duplication depuis ${lastBillQ.data.bill.bill_number} (${lastBillQ.data.bill.line_items.length} ligne(s))`,
    );
    applyLastBillTemplate(lastBillQ.data.bill);
    setLastBillAppliedFor(form.zoho_vendor_id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lastBillQ.data, lastBillQ.isFetching, form.zoho_vendor_id, validated, isNdf]);

  function applyLastBillTemplate(bill: ZohoBillTemplate) {
    // Stratégie :
    //   - Le NOMBRE de lignes vient de Claude (form.lines), donc reflète
    //     la nouvelle facture (1 ligne par poste détecté sur le PDF actuel).
    //   - Les MONTANTS viennent de Claude (et seront convertis en EUR par
    //     le backend si la facture est en devise étrangère).
    //   - La STRUCTURE (produit, compte) vient de la last-bill, appariée PAR
    //     CONTENU (TVA + libellé) et non par position : l'ordre des lignes de
    //     Claude suit le PDF, celui du template suit l'ancienne saisie, et un
    //     appariement positionnel posait les montants sur le mauvais compte
    //     dès que la facture portait deux comptes distincts.
    const template = bill.line_items;

    // Le libellé du PDF est la meilleure clé d'appariement : on le relit dans
    // l'extraction car la description du formulaire est volontairement vide
    // pour les factures fournisseurs.
    const linesForMatch = form.lines.map((l, idx) => ({
      description: l.description || extracted?.lines[idx]?.description || '',
      vat_rate: l.vat_rate,
    }));
    const matches = matchTemplateLines(linesForMatch, template);
    const ambiguous = matches.filter((m) => m == null).length;

    setForm((f) => {
      const lines =
        f.lines.length > 0
          ? f.lines.map((extractedLine, idx) => {
              // Appariement ambigu (plusieurs lignes template également
              // plausibles, de structures différentes) → on n'impute RIEN :
              // un compte vide se voit et se corrige, un compte faux non.
              const tmplIdx = f.lines.length === matches.length ? matches[idx] : null;
              const tmpl = tmplIdx == null ? null : template[tmplIdx];
              if (!tmpl) return extractedLine;
              return {
                ...extractedLine,
                // Description : on garde la valeur courante (vide par défaut,
                // ou nom commercial pour les NDF) — on ne la ré-remplit PAS
                // depuis la last-bill.
                description: extractedLine.description ?? '',
                account: extractedLine.account || tmpl.account_name || '',
                zoho_item_id: extractedLine.zoho_item_id ?? tmpl.item_id,
                zoho_item_name: extractedLine.zoho_item_name ?? tmpl.item_name,
              };
            })
          : // Pas d'extraction Claude → on duplique la last-bill telle quelle
            // (cas rare : facture sans extraction, p. ex. clé Anthropic absente).
            template.map((li) => ({
              // Vide par défaut (ce cas ne concerne pas les NDF, qui ont
              // toujours une extraction Claude).
              description: '',
              quantity: li.quantity ?? 1,
              unit_price_ht: li.rate ?? 0,
              vat_rate: li.tax_percentage ?? null,
              account: li.account_name ?? '',
              zoho_item_id: li.item_id,
              zoho_item_name: li.item_name,
            }));

      // Hérite des custom_fields du précédent bill comme défauts si vides.
      const cf = Object.fromEntries(
        bill.custom_fields.map((c) => [c.label.toLowerCase(), c.value]),
      );
      return {
        ...f,
        lines,
        charge_type: f.charge_type || cf['type de charge'] || '',
        pedagogic_allocation:
          f.pedagogic_allocation ||
          cf['affectation bilan pédagogique'] ||
          cf['affectation pédagogique'] || // fallback : anciennes bills avant le rename
          '',
        payment_mode: f.payment_mode || cf['mode de paiement'] || '',
        bank: f.bank || cf['banque'] || '',
      };
    });
    setLinesSource({
      kind: 'duplicated',
      billNumber: bill.bill_number,
      billDate: bill.date,
    });
    setAmbiguousLines(ambiguous);
    toast.info(`Template dupliqué de la facture ${bill.bill_number}`);
  }

  // ─── Conversion devise : applique le taux × unit_price_ht extrait ────────
  // À l'init (taux disponible) ET à chaque changement de taux (manuel),
  // on reconvertit DEPUIS les montants extraits originaux (pas depuis le
  // form.lines déjà éventuellement converti). Cohérent et idempotent.
  const effectiveRate =
    isForeignCurrency
      ? (form.exchange_rate != null && form.exchange_rate > 0
          ? form.exchange_rate
          : currencyRateQ.data?.rate ?? null)
      : null;
  const [convertedAtRate, setConvertedAtRate] = useState<number | null>(null);
  useEffect(() => {
    if (!isForeignCurrency) return;
    if (!effectiveRate) return;
    if (!extracted) return;
    if (validated) return; // facture déjà validée : on garde tel quel
    if (convertedAtRate === effectiveRate) return; // déjà appliqué pour ce taux
    setForm((f) => ({
      ...f,
      lines: f.lines.map((line, idx) => {
        // Le montant d'origine est porté par la ligne : le regroupement des
        // lignes identiques peut avoir changé leur nombre et leur ordre, donc
        // l'index dans `extracted.lines` n'est plus une clé fiable (fallback
        // conservé pour les factures antérieures à ce champ).
        const orig = line.original_unit_price_ht ?? extracted.lines[idx]?.unit_price_ht;
        // Si pas de pendant extrait, on garde la ligne telle quelle (cas
        // d'une ligne ajoutée manuellement par l'utilisateur).
        if (orig == null) return line;
        const eur = Math.round(orig * effectiveRate * 100) / 100;
        return { ...line, unit_price_ht: eur };
      }),
      lines_already_in_eur: true,
    }));
    setConvertedAtRate(effectiveRate);
  }, [effectiveRate, isForeignCurrency, extracted, validated, convertedAtRate]);

  // ─── Regroupement des lignes de même imputation ──────────────────────────
  // Une facture détaille souvent plusieurs postes qui atterrissent sur le MÊME
  // produit + compte + taux de TVA : Zoho n'a pas besoin de ce détail, une
  // ligne à la somme suffit. Deux taux, deux comptes, ou avec/sans TVA restent
  // évidemment des lignes distinctes (cf. `consolidateLines`).
  //
  // Le regroupement n'a lieu qu'UNE FOIS, quand la structure comptable est
  // posée (template appliqué, ou fournisseur sans historique) : plus tôt,
  // toutes les lignes portent encore le compte par défaut du fournisseur et on
  // fusionnerait des lignes que le template doit justement imputer
  // différemment. Les NDF sont exclues (1 justificatif = 1 ligne, cf. la règle
  // « 1 plein de carburant = 1 ligne »).
  const structureSettled = !form.zoho_vendor_id
    ? false
    : form.is_new_vendor
      ? true
      : lastBillQ.isFetching
        ? false
        : lastBillAppliedFor === form.zoho_vendor_id
          ? true
          : lastBillQ.data
            ? !lastBillQ.data.bill // vendor sans historique → rien à attendre
            : false;
  const [consolidatedFor, setConsolidatedFor] = useState<string | null>(null);
  useEffect(() => {
    if (isNdf) return;
    if (validated) return; // facture déjà validée : on garde tel quel
    if (!structureSettled || !form.zoho_vendor_id) return;
    if (consolidatedFor === form.zoho_vendor_id) return;
    setConsolidatedFor(form.zoho_vendor_id);
    const { lines, removed } = consolidateLines(form.lines);
    if (removed === 0) return;
    setForm((f) => ({ ...f, lines }));
    toast.info(
      `Lignes regroupées : ${form.lines.length} → ${lines.length} (même produit, compte et TVA)`,
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [structureSettled, form.zoho_vendor_id, form.lines, consolidatedFor, isNdf, validated]);

  // ─── NDF : défauts forcés au mount ───────────────────────────────────────
  // Charge / mode / banque / allocation pédagogique sont prévisibles pour une
  // NDF. due_date = invoice_date (paiement immédiat CB perso à rembourser). La
  // BANQUE dépend de la personne : Fabien règle sur QONTO, Dominique sur CIC.
  // On ne touche pas aux champs déjà remplis (manuels ou repris de validated_data).
  const ndfBank = invoice.source === 'dropbox_ndf_fabien' ? 'QONTO' : 'CIC';
  const [ndfDefaultsDone, setNdfDefaultsDone] = useState(false);
  useEffect(() => {
    if (!isNdf) return;
    if (validated) return; // facture déjà validée : on garde tel quel
    if (ndfDefaultsDone) return;
    setForm((f) => ({
      ...f,
      charge_type: f.charge_type || 'Frais internes',
      pedagogic_allocation: f.pedagogic_allocation || 'Sans distinction',
      payment_mode: f.payment_mode || 'CB',
      bank: f.bank || ndfBank,
      due_date: f.due_date || f.invoice_date || '',
    }));
    setNdfDefaultsDone(true);
  }, [isNdf, validated, ndfDefaultsDone, ndfBank]);

  // ─── Règle métier : CB / Paypal → échéance = date de facture ─────────────
  // Paiement immédiat → pas de délai. On force due_date = invoice_date dès que
  // le mode est CB ou Paypal (et qu'on a une date de facture). Couvre aussi
  // les NDF (toujours CB). Reste éditable : si l'utilisateur change le mode
  // ensuite, on ne re-force plus.
  useEffect(() => {
    if (validated) return;
    const immediate = form.payment_mode === 'CB' || form.payment_mode === 'Paypal';
    if (immediate && form.invoice_date && form.due_date !== form.invoice_date) {
      setForm((f) => ({ ...f, due_date: f.invoice_date }));
    }
  }, [form.payment_mode, form.invoice_date, form.due_date, validated]);

  // ─── Auto-fill NDF : mappe line.product (Claude) → item Zoho ─────────────
  // Pour les NDF Dominique, Claude renseigne `product` ("Carburant", "Péage",
  // "Parking" ou "Frais de déplacement"). On retrouve l'item Zoho exact par
  // son nom, et on propage purchase_account_name dans line.account. L'utilisateur
  // peut toujours modifier après — on ne remplit que si la ligne n'a pas déjà
  // d'item_id (ne ré-écrase pas une saisie manuelle).
  useEffect(() => {
    if (!isNdf) return;
    if (validated) return; // une facture déjà validée garde ses lignes
    if (!extracted) return;
    if (items.length === 0) return;
    if (ndfPrefillDone) return;

    const claudeLines = extracted.lines ?? [];
    setForm((f) => {
      const updated = f.lines.map((line, idx) => {
        if (line.zoho_item_id) return line; // déjà rempli
        const claudeLine = claudeLines[idx];
        const productName = (claudeLine as { product?: string } | undefined)?.product;
        if (!productName) return line;
        // Match strict-puis-laxiste : exact d'abord (les 4 valeurs canoniques),
        // sinon inclusion case-insensitive sur le nom de l'item.
        const target = productName.trim().toLowerCase();
        const matched =
          items.find((i) => i.name.trim().toLowerCase() === target) ??
          items.find((i) => i.name.trim().toLowerCase().includes(target));
        if (!matched) return line;
        const account =
          matched.purchase_account_name || matched.account_name || line.account || '';
        return {
          ...line,
          zoho_item_id: matched.id,
          zoho_item_name: matched.name,
          account,
        };
      });
      return { ...f, lines: updated };
    });
    setNdfPrefillDone(true);
  }, [isNdf, validated, extracted, items, ndfPrefillDone]);

  // ─── Détection automatique du supplier local pour apprentissage futur ───
  // Quand un vendor Zoho est sélectionné, on cherche un supplier local par
  // nom approchant (pour conserver le pont avec la table suppliers).
  useEffect(() => {
    if (!form.zoho_vendor_id) {
      if (form.supplier_id) setForm((f) => ({ ...f, supplier_id: null }));
      return;
    }
    const vendor = vendors.find((v) => v.id === form.zoho_vendor_id);
    if (!vendor) return;
    const match = suppliers.find(
      (s) =>
        s.name.toLowerCase().replace(/\s+/g, '') ===
        vendor.name.toLowerCase().replace(/\s+/g, ''),
    );
    setForm((f) => ({ ...f, supplier_id: match?.id ?? null }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.zoho_vendor_id, vendors, suppliers]);

  // ─── Setters ───
  const set = <K extends keyof ValidationFormState>(key: K, value: ValidationFormState[K]) =>
    setForm((f) => ({ ...f, [key]: value }));

  const setVendor = (id: string | null) => {
    const vendorName = id ? vendors.find((v) => v.id === id)?.name ?? null : null;
    setForm((f) => ({
      ...f,
      zoho_vendor_id: id,
      zoho_vendor_name: vendorName,
      // Sortir du mode "nouveau" si on choisit un vendor existant.
      is_new_vendor: id ? false : f.is_new_vendor,
      // On efface l'item Zoho global (utile seulement en mode nouveau).
      zoho_item_id: null,
    }));
    setLastBillAppliedFor(null); // force réapplication du template
    setLinesSource({ kind: 'claude' }); // reset jusqu'à ce qu'une duplication arrive

    // Apprentissage de l'alias : on mémorise la correspondance nom extrait par
    // Claude → vendor Zoho choisi, pour pré-sélectionner automatiquement les
    // prochaines factures du même fournisseur (et garantir le nom Dropbox).
    // Fire-and-forget : ne bloque pas l'UI.
    const extractedName = extracted?.supplier?.name?.trim();
    if (id && vendorName && extractedName) {
      vendorAliasesApi
        .upsert({ extracted_name: extractedName, zoho_vendor_id: id, zoho_vendor_name: vendorName })
        .catch(() => {
          /* silencieux : l'alias est un confort, pas bloquant */
        });
    }
  };

  const toggleNewVendor = (checked: boolean) => {
    setForm((f) => ({
      ...f,
      is_new_vendor: checked,
      zoho_vendor_id: checked ? null : f.zoho_vendor_id,
      zoho_item_id: checked ? f.zoho_item_id : null,
    }));
  };

  // ─── Mutations ───
  const validateAndCreateMutation = useMutation({
    mutationFn: async () => {
      await invoicesApi.validate(invoice.id, form);
      return invoicesApi.create(invoice.id);
    },
    onSuccess: (resp) => {
      qc.invalidateQueries({ queryKey: ['invoices'] });
      qc.invalidateQueries({ queryKey: ['invoice', invoice.id] });
      toast.success(`Bill Zoho créée (${resp.zoho_bill_id})`);
      // Erreurs d'archivage signalées séparément (les 2 chemins sont indépendants).
      if (resp.archive_error) {
        toast.error(`Archive fournisseur KO : ${resp.archive_error}`);
      }
      if (resp.accountant_error) {
        toast.error(`Archive KPMG KO : ${resp.accountant_error}`);
      }
      navigate(backToList);
    },
    onError: (err) => {
      // Doublon Zoho (409) → message dédié (déjà lisible). Sinon, on formate
      // les issues Zod en messages champ par champ.
      const status = (err as { status?: number }).status;
      if (status === 409) {
        toast.error((err as Error).message);
      } else {
        toast.error(formatApiError(err));
      }
    },
  });

  const rejectMutation = useMutation({
    mutationFn: (reason: string) => invoicesApi.reject(invoice.id, reason),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['invoices'] });
      toast.info('Facture rejetée');
      navigate(backToList);
    },
    onError: (err) => toast.error(`Rejet échoué : ${formatApiError(err)}`),
  });

  const deleteMutation = useMutation({
    mutationFn: (reason: string) => invoicesApi.remove(invoice.id, reason),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['invoices'] });
      toast.info('Facture supprimée');
      navigate(backToList);
    },
    onError: (err) => toast.error(`Suppression échouée : ${formatApiError(err)}`),
  });

  // ─── Confiance par champ ───
  const vendorChosen = Boolean(form.zoho_vendor_id) || form.is_new_vendor;
  const conf = (v: unknown) => fieldConfidence(v, vendorChosen);

  // ─── Validation client minimale ───
  // « TVA non récupérable — enregistrer en TTC » : proposée dans le SEUL cas où
  // Gertrude refuserait la facture faute de pouvoir rattacher sa TVA à la France.
  // Même règle que le serveur, appliquée aux lignes telles qu'elles sont à l'écran.
  const tvaNonRecuperableProposee = useMemo(() => {
    if (!extracted) return false;
    const taux = form.lines.map((l) => l.vat_rate);
    return peutDeclarerTvaNonRecuperable({
      pays: extracted.supplier.country,
      tvaPresente: tvaPresenteEffective({
        constatExtraction: extracted.tva_presente,
        tauxDesLignes: taux,
        typeDepense: extracted.detected_expense_type,
        totauxExtraits: extracted.totals,
      }),
      typeDepense: extracted.detected_expense_type,
      tvaEtrangere: extracted.tva_etrangere === true || form.lines.some((l) => tauxEtranger(l.vat_rate)),
      numeroTvaIntra: extracted.supplier.tva_intra,
      tauxDesLignes: taux,
    });
  }, [extracted, form.lines]);
  // Hors du cas, la décision ne doit pas partir : le serveur la refuserait.
  useEffect(() => {
    if (!tvaNonRecuperableProposee && form.tva_non_recuperable) {
      setForm((f) => ({ ...f, tva_non_recuperable: false }));
    }
  }, [tvaNonRecuperableProposee, form.tva_non_recuperable]);
  // Écart de quelques centimes possible si le fournisseur calcule sa TVA sur le
  // total : on le montre avant la création. Comparable seulement en euros.
  const ecartTvaNonRecuperable =
    form.tva_non_recuperable && extractedCurrency === 'EUR'
      ? ecartAvecTtcLu(
          lignesEnTtc(form.lines, (t) => t > 0),
          extracted?.totals?.total_ttc,
        )
      : null;

  const errors: string[] = [];
  // Case cochée sur des lignes sans taux : elles partiraient au HT (cf. le serveur).
  const lignesSansTaux = form.tva_non_recuperable ? motifLignesSansTaux(form.lines.map((l) => l.vat_rate)) : null;
  if (lignesSansTaux) errors.push(lignesSansTaux);
  if (!form.zoho_vendor_id && !form.is_new_vendor) {
    errors.push('Choisis un fournisseur Zoho (ou coche « Nouveau fournisseur »)');
  }
  if (form.is_new_vendor && !form.zoho_item_id) {
    errors.push('Choisis un produit Zoho pour le nouveau fournisseur');
  }
  if (!form.invoice_number) errors.push('N° de facture obligatoire');
  if (!form.invoice_date) errors.push('Date obligatoire');
  if (!form.charge_type) errors.push('Type de charge obligatoire');
  if (!form.pedagogic_allocation) errors.push('Affectation bilan pédagogique obligatoire');
  if (!form.payment_mode) errors.push('Mode de paiement obligatoire');
  if (form.lines.length === 0) errors.push('Au moins une ligne');
  // La description de ligne n'est pas obligatoire (Zoho Books ne l'exige pas
  // sur une bill fournisseur) → pas de blocage si elle est vide.
  if (form.lines.some((l) => !l.account)) {
    errors.push('Chaque ligne doit avoir un compte');
  }
  // Édition France : sur une facture qui porte de la TVA, une ligne SANS TVA
  // doit dire POURQUOI elle est exonérée. Sans motif, Zoho refuse la facture
  // (110802) ou, pire, retombe silencieusement sur « Exonéré de TVA ». Le
  // contrôle est ici, à la saisie : découvrir le problème à la création, une
  // fois la facture partie, coûte un aller-retour pour rien.
  // Exception : sur un transport de personnes, un hébergement ou des frais
  // postaux, une ligne sans TVA n'est pas exonérée — sa TVA est exclue du droit
  // à déduction, et la facture part avec une taxe à 0 %. Exiger un motif ferait
  // saisir une exonération fausse.
  const factureMixte =
    form.lines.some((l) => (l.vat_rate ?? 0) > 0) &&
    !tvaNonDeductible(extracted?.detected_expense_type);
  if (
    factureMixte &&
    form.lines.some((l) => !((l.vat_rate ?? 0) > 0) && !l.tax_exemption_code)
  ) {
    errors.push(
      'Chaque ligne sans TVA doit porter un motif d’exonération (liste sous le taux de TVA)',
    );
  }

  // Pour les factures sous-traitance, chaque ligne doit pointer sur un client.
  const isSubcontractor = form.charge_type === 'Sous-traitance (experts)';
  if (isSubcontractor && form.lines.some((l) => !l.client_books_id)) {
    errors.push('Chaque ligne doit avoir un client (« Détails des clients »)');
  }

  // ─── Sessions facturables CRM (sous-traitance) ─────────────────────────
  // Quand on est sur une facture sous-traitance avec vendor Zoho + date,
  // on interroge CRM pour récupérer les clients pour qui le consultant a
  // des sessions Conseil "A facturer" ce mois-là. Permet de pré-remplir
  // les client_books_id sans recherche manuelle.
  const billableMonth = form.invoice_date ? form.invoice_date.slice(0, 7) : '';
  const billableQ = useQuery({
    queryKey: ['experts', 'billable-sessions', form.zoho_vendor_id, billableMonth],
    queryFn: () => expertsApi.billableSessions(form.zoho_vendor_id!, billableMonth),
    enabled:
      isSubcontractor &&
      !!form.zoho_vendor_id &&
      /^\d{4}-\d{2}$/.test(billableMonth),
    staleTime: 60_000,
  });

  // ─── Vendor hint (nom extrait par Claude si pas matché) ───
  const extractedVendorName = extracted?.supplier.name;
  const noMatchHint =
    !form.zoho_vendor_id && extractedVendorName
      ? `Claude a extrait « ${extractedVendorName} » : non trouvé dans Zoho.`
      : undefined;

  // ─── Render ───
  return (
    <div className="flex flex-col gap-4">
      {/* Fournisseur Zoho */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <Field label="Fournisseur (Zoho)" confidence={conf(form.zoho_vendor_id || form.is_new_vendor)}>
          {vendorsQ.isLoading ? (
            <div className="text-xs text-slate-500 inline-flex items-center gap-2">
              <Spinner /> Chargement de la liste Zoho…
            </div>
          ) : vendorsQ.isError ? (
            <p className="text-xs text-red-600">Erreur Zoho : {String(vendorsQ.error)}</p>
          ) : (
            <>
              <VendorSelect
                vendors={vendors}
                value={form.zoho_vendor_id}
                onChange={setVendor}
                disabled={form.is_new_vendor}
                hint={noMatchHint}
              />
              <label className="mt-1.5 inline-flex items-center gap-2 text-xs text-slate-700">
                <input
                  type="checkbox"
                  checked={form.is_new_vendor}
                  onChange={(e) => toggleNewVendor(e.target.checked)}
                  className="rounded"
                />
                Nouveau fournisseur (sera créé dans Zoho à la validation)
              </label>
            </>
          )}
        </Field>

        {form.is_new_vendor ? (
          <Field label="Produit Zoho (lignes)" confidence={conf(form.zoho_item_id)}>
            {itemsQ.isLoading ? (
              <div className="text-xs text-slate-500 inline-flex items-center gap-2">
                <Spinner /> Chargement…
              </div>
            ) : (
              <ItemSelect
                items={items}
                value={form.zoho_item_id}
                onChange={(id) => set('zoho_item_id', id)}
              />
            )}
          </Field>
        ) : (
          <Field label="N° facture" confidence={conf(form.invoice_number)}>
            <TextInput value={form.invoice_number} onChange={(v) => set('invoice_number', v)} />
          </Field>
        )}

        {form.is_new_vendor && (
          <Field label="N° facture" confidence={conf(form.invoice_number)}>
            <TextInput value={form.invoice_number} onChange={(v) => set('invoice_number', v)} />
          </Field>
        )}

        <Field label="Date facture" confidence={conf(form.invoice_date)}>
          <TextInput type="date" value={form.invoice_date} onChange={(v) => set('invoice_date', v)} />
        </Field>

        <Field label="Échéance" confidence={conf(form.due_date)}>
          <TextInput type="date" value={form.due_date} onChange={(v) => set('due_date', v)} />
        </Field>
      </div>

      {lastBillQ.isFetching && form.zoho_vendor_id && (
        <p className="text-xs text-slate-500 italic inline-flex items-center gap-2">
          <Spinner /> Récupération de la dernière facture du fournisseur…
        </p>
      )}
      {lastBillQ.data?.has_history === false && form.zoho_vendor_id && (
        <p className="text-xs text-amber-700 italic">
          Aucune facture précédente pour ce fournisseur : saisis manuellement.
        </p>
      )}

      <hr className="border-slate-200" />

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <Field label="Type de charge" confidence={conf(form.charge_type)}>
          <Select
            value={form.charge_type}
            onChange={(v) => set('charge_type', v)}
            options={referenceData.charge_types}
          />
        </Field>

        <Field label="Affectation bilan pédagogique" confidence={conf(form.pedagogic_allocation)}>
          <Select
            value={form.pedagogic_allocation}
            onChange={(v) => set('pedagogic_allocation', v)}
            options={referenceData.pedagogic_allocations}
          />
        </Field>

        <Field label="Mode de paiement" confidence={conf(form.payment_mode)}>
          <Select
            value={form.payment_mode}
            onChange={(v) => set('payment_mode', v)}
            options={referenceData.payment_modes}
          />
        </Field>

        <Field label="Banque" confidence={conf(form.bank)}>
          <Select
            value={form.bank}
            onChange={(v) => set('bank', v)}
            options={referenceData.banks}
          />
        </Field>
      </div>

      {/* Encart conversion devise (uniquement si facture en devise ≠ EUR) */}
      {isForeignCurrency && (
        <div className="rounded-md bg-amber-50 ring-1 ring-inset ring-amber-200 px-3 py-2 text-sm">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <span className="text-amber-900">
              💱 Facture en <strong>{extractedCurrency}</strong>. Conversion vers EUR pour Zoho.
            </span>
            {currencyRateQ.isLoading ? (
              <span className="text-xs text-amber-700 inline-flex items-center gap-2">
                <Spinner /> Récupération du taux…
              </span>
            ) : currencyRateQ.data ? (
              <div className="flex items-center gap-2 text-xs">
                <label className="flex items-center gap-1.5">
                  <span className="text-amber-900">1 {extractedCurrency} =</span>
                  <input
                    type="text"
                    inputMode="decimal"
                    value={form.exchange_rate ?? currencyRateQ.data.rate}
                    onChange={(e) => {
                      const raw = e.target.value.replace(',', '.').trim();
                      const v = raw === '' ? null : Number(raw);
                      set(
                        'exchange_rate',
                        v != null && Number.isFinite(v) && v > 0 ? v : null,
                      );
                    }}
                    className="w-24 rounded border border-amber-300 px-2 py-0.5 text-right tabular-nums focus:outline-none focus:ring-2 focus:ring-amber-500 bg-white"
                  />
                  <span className="text-amber-900">EUR</span>
                </label>
                <span className="text-amber-700">
                  ({form.exchange_rate != null ? 'manuel' : currencyRateQ.data.source}
                  {currencyRateQ.data.rateDate ? ` · ${currencyRateQ.data.rateDate}` : ''})
                </span>
                {/* Aperçu sur le total extrait, à titre indicatif */}
                {extracted?.totals && (
                  <span className="ml-2 text-amber-900 tabular-nums">
                    {(extracted.totals.total_ttc ?? 0).toFixed(2)} {extractedCurrency} →{' '}
                    <strong>
                      {(
                        (extracted.totals.total_ttc ?? 0) *
                        (form.exchange_rate ?? currencyRateQ.data.rate)
                      ).toFixed(2)}{' '}
                      EUR
                    </strong>
                  </span>
                )}
              </div>
            ) : currencyRateQ.isError ? (
              <span className="text-xs text-red-700">
                Taux indisponible : {String(currencyRateQ.error)}
              </span>
            ) : null}
          </div>
        </div>
      )}

      <hr className="border-slate-200" />

      <div>
        <div className="flex items-center justify-between mb-2">
          <h3 className="font-medium">Lignes</h3>
          {extracted?.totals && (
            <span className="text-xs text-slate-500 tabular-nums">
              Extrait : HT {(extracted.totals.total_ht ?? 0).toFixed(2)} · TVA{' '}
              {(extracted.totals.total_vat ?? 0).toFixed(2)} · TTC{' '}
              {(extracted.totals.total_ttc ?? 0).toFixed(2)} {extractedCurrency}
            </span>
          )}
        </div>

        {/* Bandeau source des lignes */}
        <div className="mb-2 text-xs rounded-md px-3 py-1.5 ring-1 ring-inset bg-slate-50 ring-slate-200 text-slate-700">
          {linesSource.kind === 'claude' ? (
            <>📋 Lignes extraites par Claude</>
          ) : (
            <>
              📋 Lignes dupliquées depuis la facture{' '}
              <strong>{linesSource.billNumber}</strong>
              {linesSource.billDate ? ` du ${linesSource.billDate}` : ''}
            </>
          )}
        </div>

        {/* Structure non reprise faute d'appariement fiable : on préfère un
            compte vide, visible, à un compte faux appliqué silencieusement. */}
        {ambiguousLines > 0 && (
          <div className="mb-2 text-xs rounded-md px-3 py-1.5 ring-1 ring-inset bg-amber-50 ring-amber-200 text-amber-900">
            ⚠️ {ambiguousLines === 1 ? '1 ligne n’a' : `${ambiguousLines} lignes n’ont`} pas pu
            être rapprochée{ambiguousLines > 1 ? 's' : ''} de la facture précédente (plusieurs
            imputations possibles) : renseigne le produit et le compte à la main.
          </div>
        )}

        {/* Panneau Sessions CRM (sous-traitance uniquement) */}
        {isSubcontractor && billableQ.data && (
          <BillableSessionsPanel
            data={billableQ.data}
            lineCount={form.lines.length}
            onApply={(clientsInOrder) => {
              setForm((f) => ({
                ...f,
                lines: f.lines.map((l, idx) => {
                  const c = clientsInOrder[idx];
                  if (!c || !c.client_books_id) return l;
                  return { ...l, client_books_id: c.client_books_id, client_name: c.client_name };
                }),
              }));
            }}
          />
        )}
        {isSubcontractor && billableQ.isLoading && (
          <p className="mb-2 text-xs text-slate-500 inline-flex items-center gap-2">
            <Spinner /> Recherche des sessions CRM du consultant…
          </p>
        )}
        {isSubcontractor && billableQ.isError && (
          <div className="mb-2 text-xs text-red-700 rounded-md px-3 py-1.5 ring-1 ring-inset bg-red-50 ring-red-200">
            Sessions CRM non chargées : {String(billableQ.error)}
          </div>
        )}

        {accountsQ.isLoading || itemsQ.isLoading ? (
          <p className="text-xs text-slate-500 inline-flex items-center gap-2">
            <Spinner /> Chargement produits & comptes Zoho…
          </p>
        ) : (
          <LineItemsTable
            lines={form.lines}
            items={items}
            accounts={accounts}
            onChange={(lines) => set('lines', lines)}
            showClientColumn={isSubcontractor}
            typeDepense={extracted?.detected_expense_type}
          />
        )}

        {tvaNonRecuperableProposee && (
          <label className="mt-3 flex items-start gap-2 rounded-md bg-amber-50 p-3 text-sm ring-1 ring-inset ring-amber-200">
            <input
              type="checkbox"
              className="mt-0.5"
              checked={form.tva_non_recuperable === true}
              onChange={(e) => set('tva_non_recuperable', e.target.checked)}
            />
            <span className="text-[#926B00]">
              <strong>TVA non récupérable — enregistrer en TTC</strong>
              <br />
              Ce fournisseur étranger facture de la TVA sans numéro de TVA français («{' '}
              {extracted?.supplier.tva_intra || 'aucun numéro lu'} »). Cochez si cette TVA ne peut pas être récupérée : la
              facture sera créée au montant TTC payé, avec la taxe « TVA non déductible ». Si un numéro FR… figure sur la
              facture, ne cochez pas : relancez l’extraction.
              {lignesSansTaux && (
                <>
                  <br />
                  <strong className="text-[#C0423F]">{lignesSansTaux}</strong>
                </>
              )}
              {ecartTvaNonRecuperable !== null && ecartTvaNonRecuperable !== 0 && (
                <>
                  <br />
                  Écart de {ecartTvaNonRecuperable.toFixed(2)} € avec le TTC de la facture (arrondi ligne par ligne) : il
                  sera indiqué dans la note de la facture.
                </>
              )}
            </span>
          </label>
        )}
      </div>

      {/* Erreurs / Actions */}
      <div className="mt-2 flex flex-col gap-3">
        {errors.length > 0 && (
          <ul className="text-xs text-red-600 list-disc pl-5">
            {errors.map((e) => (
              <li key={e}>{e}</li>
            ))}
          </ul>
        )}
        {validateAndCreateMutation.isError && (
          <div className="rounded-md bg-red-50 ring-1 ring-inset ring-red-200 px-3 py-2 text-xs text-red-800 whitespace-pre-line">
            {formatApiError(validateAndCreateMutation.error)}
          </div>
        )}

        <div className="flex flex-wrap items-center justify-between gap-2 border-t border-slate-200 pt-3">
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => setRejectOpen(true)}
              disabled={
                validateAndCreateMutation.isPending ||
                rejectMutation.isPending ||
                deleteMutation.isPending
              }
              className="px-3 py-1.5 rounded-md text-sm font-medium text-red-700 ring-1 ring-red-200 hover:bg-red-50 disabled:opacity-50"
            >
              ✕ Rejeter
            </button>
            <button
              type="button"
              onClick={() => setDeleteOpen(true)}
              disabled={
                validateAndCreateMutation.isPending ||
                rejectMutation.isPending ||
                deleteMutation.isPending
              }
              title="Supprimer définitivement"
              className="px-3 py-1.5 rounded-md text-sm font-medium text-slate-600 ring-1 ring-slate-200 hover:bg-slate-50 disabled:opacity-50"
            >
              🗑️ Supprimer
            </button>
          </div>

          <button
            type="button"
            onClick={() => validateAndCreateMutation.mutate()}
            disabled={errors.length > 0 || validateAndCreateMutation.isPending}
            className="px-4 py-1.5 rounded-md text-sm font-medium bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-50 inline-flex items-center gap-2"
          >
            {validateAndCreateMutation.isPending && <Spinner />}
            {validateAndCreateMutation.isPending ? 'Création…' : '✓ Valider et créer'}
          </button>
        </div>
      </div>

      <RejectModal
        open={rejectOpen}
        onClose={() => setRejectOpen(false)}
        onConfirm={(reason) => rejectMutation.mutate(reason)}
        busy={rejectMutation.isPending}
      />

      <DeleteModal
        open={deleteOpen}
        onClose={() => setDeleteOpen(false)}
        onConfirm={(reason) => deleteMutation.mutate(reason)}
        busy={deleteMutation.isPending}
      />
    </div>
  );
}
